import { Route, Plane, TrainFront, Building2, MapPin, ExternalLink } from "lucide-react";
import Reveal from "./Reveal";
import { SITE, CONNECTIVITY } from "@/data/content";

const ICONS = { road: Route, plane: Plane, train: TrainFront, building: Building2 };

export default function LocationSection() {
  return (
    <section id="location" className="py-24 lg:py-32 bg-white border-b border-border" data-testid="location-section">
      <div className="container-luxe">
        <div className="grid lg:grid-cols-2 gap-14 items-stretch">
          <div>
            <Reveal>
              <p className="eyebrow mb-4">Location &amp; Connectivity</p>
              <h2 className="font-serif font-semibold text-navy text-4xl sm:text-5xl leading-[1.05] tracking-tight">
                Dholera, Gujarat — Connected by Design
              </h2>
              <p className="mt-5 text-body leading-relaxed">
                Dholera Special Investment Region sits about 100 km southwest of Ahmedabad, linked by a new-generation expressway and a greenfield international airport under development.
              </p>
            </Reveal>

            <Reveal delay={0.1} className="mt-10 relative pl-8">
              <span className="absolute left-[9px] top-2 bottom-2 w-px bg-gradient-to-b from-gold via-gold/40 to-transparent" />
              <div className="space-y-7">
                {[
                  { name: "Ahmedabad", detail: "Starting point — Sardar Patel Ring Road (Sarkhej) on the expressway" },
                  { name: "Dholera SIR", detail: "~83 km, about 45 minutes via the operational Ahmedabad–Dholera Expressway" },
                  { name: "Dholera International Airport (Navagam)", detail: "Approx. 20 km from the SIR boundary — Phase-1 complete, trials underway" },
                ].map((n, i) => (
                  <div key={n.name} className="relative" data-testid={`route-node-${i}`}>
                    <span className={`absolute -left-8 top-1 w-[19px] h-[19px] rounded-full border-2 flex items-center justify-center ${i === 1 ? "border-gold bg-gold" : "border-gold bg-white"}`}>
                      {i === 1 && <span className="w-1.5 h-1.5 bg-navy rounded-full" />}
                    </span>
                    <h3 className="font-serif text-xl text-navy font-semibold">{n.name}</h3>
                    <p className="text-body text-sm mt-1 leading-relaxed">{n.detail}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <div className="mt-10 space-y-5">
              {CONNECTIVITY.map((c, i) => {
                const Icon = ICONS[c.icon];
                return (
                  <Reveal key={c.title} delay={0.06 * i} className="flex gap-4 items-start bg-paper border border-border rounded-sm p-5 hover:border-gold/50 transition-colors duration-300">
                    <span className="w-10 h-10 shrink-0 bg-navy text-gold flex items-center justify-center rounded-sm">
                      <Icon className="w-5 h-5" />
                    </span>
                    <div>
                      <h3 className="font-semibold text-navy text-sm">{c.title}</h3>
                      <p className="text-body text-sm mt-1 leading-relaxed">{c.detail}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>

            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.16em] text-navy/45">
              Source: DSIRDA · PIB · Press reports (2026). Distances and times are approximate.
            </p>
          </div>

          <Reveal delay={0.15} y={44} className="relative">
            <div className="sticky top-28">
              <div className="clip-frame border border-gold/40 p-2 bg-paper">
                <iframe
                  title="Dholera Special Investment Region, Gujarat — Google Maps"
                  src={SITE.mapsEmbed}
                  className="w-full h-[420px] lg:h-[560px] grayscale-[0.25] contrast-[1.05]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  data-testid="google-maps-embed"
                />
              </div>
              <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
                <p className="flex items-center gap-2 text-sm text-body">
                  <MapPin className="w-4 h-4 text-gold-dark" /> Dholera SIR, Gujarat 364250
                </p>
                <a
                  href={SITE.mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-google-maps-link"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-navy border-b border-gold hover:text-gold-dark transition-colors duration-300"
                >
                  Open in Google Maps <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
